// src/components/dnl/ConfirmCaptureDialog.tsx
/**
 * Confirmation Dialog for View Capture
 * Phase 5: Alternative Views Feature
 * 
 * Warns users when capturing would overwrite existing view configuration
 */

import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

export interface ConfirmCaptureDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Which view is being captured ('A' or 'B') */
  targetView: 'A' | 'B';
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Dark mode */
  isDark?: boolean;
}

export const ConfirmCaptureDialog: React.FC<ConfirmCaptureDialogProps> = ({
  isOpen,
  targetView,
  onConfirm,
  onCancel,
  isDark = false,
}) => {
  if (!isOpen) return null;

  const overlayClass = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
  const dialogClass = `
    w-full max-w-md mx-4 rounded-lg shadow-xl border
    ${isDark
      ? 'bg-gray-800 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900'
    }
  `;
  const headerClass = `
    flex items-start gap-3 p-4 border-b
    ${isDark ? 'border-gray-700' : 'border-gray-200'}
  `;
  const buttonClass = (variant: 'danger' | 'secondary') => `
    px-4 py-2 rounded font-medium transition-colors
    ${variant === 'danger'
      ? isDark
        ? 'bg-red-600 text-white hover:bg-red-700'
        : 'bg-red-500 text-white hover:bg-red-600'
      : isDark
        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }
  `;

  return (
    <div className={overlayClass} onClick={onCancel}>
      <div className={dialogClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={headerClass}>
          <AlertTriangle size={24} className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Overwrite View {targetView}?</h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              View {targetView} is already configured. Capturing will replace the existing configuration.
            </p>
          </div>
          <button
            onClick={onCancel}
            className={`p-1 rounded hover:bg-black/10 ${isDark ? 'hover:bg-white/10' : ''}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <p className="font-medium mb-2">Current View {targetView} configuration:</p>
            <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <li>Will be permanently replaced</li>
              <li>Cannot be undone</li>
              <li>Any saved points and labels will be lost</li>
            </ul>
          </div>

          <div className={`
            p-3 rounded border
            ${isDark
              ? 'bg-yellow-900/20 border-yellow-700/50 text-yellow-200'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
            }
          `}>
            <p className="text-sm">
              <strong>Tip:</strong> Make sure you're happy with the current number line setup before capturing.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className={`
          flex gap-3 justify-end p-4 border-t
          ${isDark ? 'border-gray-700' : 'border-gray-200'}
        `}>
          <button onClick={onCancel} className={buttonClass('secondary')}>
            Cancel
          </button>
          <button onClick={onConfirm} className={buttonClass('danger')}>
            Overwrite View {targetView}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCaptureDialog;






